import React, { useEffect, useMemo, useState, useRef } from "react";
import { View, StyleSheet, Animated } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { useRoute, useNavigation } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { ExitDoor } from "../components/svgs/common";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import ChatMenuContainer from "../containers/chatmenu/ChatMenuContainer/ChatMenuContainer";
import ChatContainer from "../containers/chat/ChatContainer/ChatContainer";
import ChatBarContainer from "../containers/navigation/ChatBarContainer/ChatBarContainer";
import { useGetChatByObjectIdQuery, useGetChatsQuery, useGetCourseByIdQuery } from "../graphql/generated/graphql";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { Context, useChatBar, ConversationType } from "../contexts/ChatBarContext";
import { useAppState, actions } from "../contexts/AppStateContext";
import AsyncStorage from "@react-native-async-storage/async-storage";
import useWebSocket from "../hooks/useWebSocket";
import UpgradeModalContainer from "../containers/general/UpgradeModalContainer/UpgradeModalContainer";
import AiTutorCompleteModal from "../containers/customCoursework/AiTutorCompleteModal";
import { sortResourceItemsOfModules } from "../utils/sortResourceItemsOfModules";
import { MenuCircle } from "../components/svgs/common";
import BlankLayout from "../layouts/BlankLayout";
import { shadowStyles } from "../styles/shadow";
import LottieView from "lottie-react-native";
import LottieUrl from "../assets/lottie/aiTutorChatLoadingLottie.json";

const TIMER_DURATION = 1500;

const returnRandomIntroText = () => {
  const intros = [
    "Analyzing your progress and tailoring the conversation...",
    "The more you lean into learning, the faster you'll progress through your objectives.",
    "We detect when answers are copied from AI or other sources - please be original in your responses.",
    "Ask questions when you're curious! The AI loves when you dig deeper into topics.",
    "Don't worry about making mistakes - they're part of the learning process and help you grow.",
    "Take your time to think through each question. Quality of thought matters more than speed.",
    "Your previous conversations help the AI understand your learning style - each session gets more personalized.",
    "Try explaining concepts in your own words - it helps solidify your understanding.",
    "The AI remembers where you've struggled before and will help you strengthen those areas.",
    "Engage authentically - we can tell when responses aren't genuinely your own thinking.",
    "Every question is an opportunity to deepen your understanding of the subject.",
    "Your learning journey is unique - no two students will have the same experience here.",
    "Think critically about each concept before responding - deeper thinking leads to better learning.",
    "Feel free to ask for clarification if something doesn't make sense.",
    "The more engaged you are in the conversation, the more the AI can help you learn.",
    "Building on your past conversations to create the perfect learning experience for you.",
    "Quality responses help the AI understand your thought process and adapt accordingly.",
    "Each interaction helps build a more personalized and effective learning path.",
    "Curiosity and genuine engagement are the keys to accelerating your progress.",
    "Preparing an adaptive conversation based on your unique learning patterns...",
  ];
  return intros[Math.floor(Math.random() * intros.length)];
};

const CustomCourseAITutorChatView = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course AITutor">>();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { isMedium } = useResponsiveScreen();
  const { resource_id, course_id, integration_id, resource_type, module_id } = route.params;
  const { sendMessage } = useWebSocket();
  const {
    setContext,
    setActiveContextAndObjectId,
    setConversationType,
    setSchoolMessage,
    mapRouteToContext,
    setMetaIsLoading,
    state: chatBarState,
  } = useChatBar();
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [aiTutorCompleteModalVisible, setAiTutorCompleteModalVisible] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const { data: chatsData } = useGetChatsQuery();
  const { state: appState, dispatch } = useAppState();

  const [showIntro, setShowIntro] = useState(true);
  const [minLoadingTimePassed, setMinLoadingTimePassed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMinLoadingTimePassed(true), TIMER_DURATION);
    return () => clearTimeout(timer);
  }, []);

  const { data: courseData } = useGetCourseByIdQuery({
    variables: {
      id: course_id,
      withModules: true,
    },
  });

  const {
    data: chatByObjectIdData,
    loading: chatByObjectIdLoading,
    refetch: chatByObjectIdRefetch,
  } = useGetChatByObjectIdQuery({
    variables: {
      objectId: resource_id,
      context: Context.AITUTOR,
    },
    skip: !resource_id,
  });

  const slideAnim = useRef(new Animated.Value(0)).current; // Start at the default position

  useEffect(() => {
    if (chatBarState.activeObjectId) {
      chatByObjectIdRefetch({
        objectId: chatBarState.activeObjectId,
        context: chatBarState.activeContext,
      });
    }
  }, [appState.meta.chatHistory]);

  useEffect(() => {
    const completedAiTutorId = appState?.meta?.completedAiTutorId;
    if (completedAiTutorId && completedAiTutorId === resource_id) {
      setAiTutorCompleteModalVisible(true);
      dispatch({
        type: actions.RESET_COMPLETED_AITUTOR_ID,
      });
    }
  }, [appState.meta.completedAiTutorId]);

  const handleGetHelpFromAxio = async (id: string) => {
    const token = await AsyncStorage.getItem("accessToken");

    sendMessage({
      meta: {
        token: token,
        abort: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isLoading: true,
        displayMessage: false,
      },
      state: {
        general: "SCHOOL",
        context: mapRouteToContext("Custom Resource Student"),
        params: {
          resource_type: "AITutor",
          resource_id: id,
          course_id,
          integration_id,
        },
        school: {
          contentType: "AITutor",
          contentId: id,
          classId: course_id,
          integrationId: integration_id,
        },
      },
      content: {
        message: chatByObjectIdData?.getChatByObjectId?.id
          ? "Let's continue where we left off"
          : "Please begin by introducing me to this topic",
        author: "HUMAN",
        type: null,
      },
    });
    setMetaIsLoading(true);
  };

  useEffect(() => {
    if (chatByObjectIdLoading || chatBarState.llmMessage.meta.isLoading) {
      return;
    }

    if (!chatByObjectIdData?.getChatByObjectId?.id) {
      handleGetHelpFromAxio(resource_id);
    } else {
      if (minLoadingTimePassed) {
        setShowIntro(false);
      }
    }
  }, [chatByObjectIdData, chatByObjectIdLoading, chatBarState.llmMessage.meta.isLoading, minLoadingTimePassed]);

  useEffect(() => {
    setConversationType(ConversationType.SCHOOL);
    setActiveContextAndObjectId({
      context: Context.AITUTOR,
      objectId: resource_id,
    });
    setContext({
      context: Context.AITUTOR,
      params: {
        resource_id: resource_id,
        course_id,
        integration_id,
        resource_type: "AITutor",
      },
    });
    setSchoolMessage({
      contentType: resource_type,
      contentId: resource_id,
      classId: course_id,
      integrationId: integration_id,
    });
  }, [resource_id, course_id, integration_id, resource_type]);

  const handleExitPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      const moduleId = route.params?.module_id;
      if (moduleId && typeof moduleId === "string") {
        const integrationId =
          typeof route.params?.integration_id === "string" ? route.params.integration_id : course_id;
        navigation.navigate("Custom Module Student", {
          module_id: moduleId,
          course_id: course_id,
          integration_id: integrationId,
        });
      } else {
        navigation.navigate("Custom Course Student", {
          course_id,
          integration_id,
        });
      }
    }
  };

  const handleChatPress = async (chat) => {
    dispatch({
      type: actions.SET_CONTENT,
      payload: { message: "" },
    });

    setConversationType(ConversationType.SCHOOL);

    const { resource_id, course_id, integration_id, resource_type } = chat.params;

    setSchoolMessage({
      contentType: resource_type,
      contentId: resource_id,
      classId: course_id,
      integrationId: integration_id,
    });

    setContext({
      context: Context.AITUTOR,
      params: chat.params as { [key: string]: string },
    });

    setActiveContextAndObjectId({
      context: Context.AITUTOR,
      objectId: chat.objectId,
    });
  };

  const handleCloseUpgradeModals = () => {
    setUpgradeModalVisible(false);
  };

  const formattedModules = useMemo(() => {
    return courseData?.getCourseById.data[0].modules.map((module) => {
      const aiTutors = module.aiTutors.map((aiTutor) => ({
        ...aiTutor,
        resourceType: "aiTutor",
      }));

      const axioResources = module.axioResources.map((resource) => ({
        ...resource,
        resourceType: "axioResource",
      }));

      const assignments = module.assignments.map((assignment) => ({
        ...assignment,
        resourceType: "assignment",
      }));

      const assessments = module.assessment ? [{ ...module.assessment, resourceType: "assessment" }] : [];

      const projects = module.projects.map((project) => ({
        ...project,
        resourceType: "project",
      }));

      const allResources = [...aiTutors, ...axioResources, ...assignments, ...assessments, ...projects];

      return {
        moduleId: module.id,
        resources: sortResourceItemsOfModules(allResources, module.resourceOrder),
      };
    });
  }, [courseData]);

  const currentModuleIndex = formattedModules?.findIndex((m) => m.moduleId === module_id);

  const currentModule = formattedModules?.[currentModuleIndex ?? -1];

  const currentResourceIndex = currentModule?.resources.findIndex((r) => r.id === resource_id);

  const nextActivity = useMemo(() => {
    if (
      !formattedModules ||
      currentModuleIndex === null ||
      currentModuleIndex < 0 ||
      !currentModule ||
      currentResourceIndex === null ||
      currentResourceIndex < 0
    ) {
      // next available activity cannot be found
      return null;
    }

    if (currentResourceIndex < currentModule.resources.length - 1) {
      // next activity is in the current module
      return currentModule.resources[currentResourceIndex + 1];
    }

    const nextModule = formattedModules[currentModuleIndex + 1];
    if (nextModule && nextModule.resources.length > 0) {
      // this was the last activity in the module, returning first activity of the next module
      return nextModule.resources[0];
    }

    // no next activity exists
    return null;
  }, [formattedModules, currentModuleIndex, currentModule, currentResourceIndex]);

  const handleNextActivityPress = () => {
    if (!nextActivity) return;

    setAiTutorCompleteModalVisible(false);

    const targetModuleId = formattedModules?.find((m) => m.resources.some((r) => r.id === nextActivity.id))?.moduleId;

    switch (nextActivity.resourceType) {
      case "aiTutor":
        navigation.navigate("Custom Course AITutor", {
          resource_type: "aiTutor",
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;
      case "assignment":
        navigation.navigate("Custom Assignment Student", {
          resource_type: "assignment",
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;

      case "project":
        navigation.navigate("Project", {
          id: nextActivity.id,
          courseId: course_id,
          integrationId: integration_id,
        });
        break;

      case "axioResource":
      case "assessment":
        navigation.navigate("Custom Resource Student", {
          resource_type: nextActivity.resourceType,
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;

      default:
        console.warn("⚠️ Unknown next activity type", nextActivity);
    }
  };

  const handleBackPress = () => {
    setAiTutorCompleteModalVisible(false);
    handleExitPress();
  };

  const handleMenuToggle = () => {
    if (menuVisible) {
      // Slide out to the left (hide)
      Animated.timing(slideAnim, {
        toValue: 0, // Back to the initial position
        duration: 300,
        useNativeDriver: true,
      }).start(() => setMenuVisible(false)); // Hide after animation
    } else {
      setMenuVisible(true); // Set visible before starting the animation
      // Slide in from the left
      Animated.timing(slideAnim, {
        toValue: 270, // Slide the container to the right
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  useEffect(() => {
    if (appState.meta.isAtDayLimit || appState.meta.isAtLifetimeLimit) {
      setUpgradeModalVisible(true);
    }
  }, [appState.meta.isAtDayLimit, appState.meta.isAtLifetimeLimit]);

  const selectedResource = currentModule?.resources.find((r) => r.id === resource_id);

  return (
    <BlankLayout>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <View style={styles.exitButtonContainer}>
              <CustomButton
                leftIcon={<ExitDoor />}
                onPress={handleExitPress}
                size="s"
                styleType="transparent"
                text=""
                rightIcon={null}
                style={{}}
                textStyle={{}}
                disabled={false}
                textProps={{}}
              />
            </View>
            {!isMedium && (
              <CustomButton
                leftIcon={<MenuCircle fill="#667085" style={styles.menuCircle} />}
                onPress={handleMenuToggle}
                size="s"
                styleType="transparent"
                text=""
                rightIcon={null}
                style={{}}
                textStyle={{}}
                disabled={false}
                textProps={{}}
              />
            )}
            {/* Display the course name */}
            <CustomSpacing type="horizontal" size="m" />
            <CustomText text={selectedResource?.title} useTranslationText={false} weight="bold" size="m" />
          </View>
        </View>

        {/* Main Content */}
        <View style={styles.content}>
          {/* Chat Menu */}
          {isMedium ? (
            <View style={styles.menu}>
              <ChatMenuContainer chatsData={chatsData} onChatPress={handleChatPress} onMobileChatPress={() => {}} />
            </View>
          ) : (
            <Animated.View style={[styles.chatMenuContainer, { transform: [{ translateX: slideAnim }] }]}>
              <ChatMenuContainer
                chatsData={chatsData}
                onChatPress={handleChatPress}
                onMobileChatPress={handleMenuToggle}
              />
            </Animated.View>
          )}

          {/* Chat Area */}
          <View style={styles.chatArea}>
            {showIntro ? (
              <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                <View style={[styles.loaderContainer, shadowStyles.allAroundShadow]}>
                  <LottieView autoPlay source={LottieUrl} speed={1} loop={true} style={{ width: 100, height: 100 }} />
                  <CustomText
                    text="Loading your personalized learning experience"
                    useTranslationText={false}
                    size="l"
                    weight="bold"
                    style={{ textAlign: "center" }}
                  />
                  <CustomText
                    text={returnRandomIntroText()}
                    useTranslationText={false}
                    size="m"
                    weight="regular"
                    style={{ marginTop: 10, textAlign: "center" }}
                  />
                </View>
              </View>
            ) : (
              <ChatContainer chatData={chatByObjectIdData?.getChatByObjectId} />
            )}
            <View style={styles.bar}>
              <ChatBarContainer />
            </View>
          </View>
        </View>

        <UpgradeModalContainer visible={upgradeModalVisible} onClose={handleCloseUpgradeModals} />
        <AiTutorCompleteModal
          visible={aiTutorCompleteModalVisible}
          onBackPress={handleBackPress}
          onNextPress={handleNextActivityPress}
          hasNextActivity={Boolean(nextActivity)}
        />
      </View>
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: "#D0D5DD",
    borderBottomWidth: 1,
  },
  headerLeft: {
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  exitButtonContainer: {
    borderRightColor: "#D0D5DD",
    borderRightWidth: 1,
    paddingHorizontal: 10,
  },
  content: {
    flex: 1,
    flexDirection: "row",
  },
  menu: {
    width: 270,
  },
  chatArea: {
    flex: 1,
    position: "relative",
  },
  bar: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignSelf: "center",
    flexDirection: "row",
  },
  loaderContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 16,
    borderWidth: 0,
    maxWidth: 500,
    width: "90%",
    padding: 40,
  },
  chatMenuContainer: {
    position: "absolute",
    top: 0,
    left: -270,
    bottom: 56,
    zIndex: 100,
  },
  menuCircle: {
    width: 28,
    height: 28,
  },
});

export default CustomCourseAITutorChatView;
